import React, { useEffect, useState, useCallback } from "react";
import {
  Dna, Activity, Scissors, Waves, RefreshCw, Globe, Radio,
  MessageSquare, Users, Github, GitBranch, Brain, TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";

const RES_META = {
  web: { icon: Globe, label: "Web" },
  hive: { icon: Waves, label: "Hive Bus" },
  radar: { icon: Radio, label: "Frontier Radar" },
  conversations: { icon: MessageSquare, label: "Conversations" },
  people: { icon: Users, label: "Person Memory" },
  github: { icon: Github, label: "GitHub" },
};

function ago(iso) {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function Sparkline({ series, field, color = "#8fd0d6" }) {
  const vals = (series || []).map((p) => p[field] || 0);
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const w = 120, h = 30;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={w} cy={h - ((vals[vals.length - 1] - min) / range) * h} r="2.2" fill={color} />
    </svg>
  );
}

const Stat = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-[9px] font-mono-data uppercase tracking-wider text-slate-500">{label}</span>
    </div>
    <p className="text-xl font-display font-bold text-white leading-none">{value}</p>
    {sub && <p className="text-[9px] font-mono-data text-slate-500 mt-1">{sub}</p>}
  </div>
);

function RegenTimeline({ tl }) {
  const [hover, setHover] = useState(null);
  const days = tl?.days || [];
  if (days.length < 2) return null;
  const W = days.length * 12, H = 150, MID = 84, UP = 72, DOWN = 58;
  const maxBorn = Math.max(1, ...days.map((d) => d.neurons_born || 0));
  const maxLost = Math.max(1, ...days.map((d) => (d.synapses_pruned || 0) + (d.synapses_decayed || 0)));
  const tot = tl?.totals || {};
  const lbl = (i) => days[i]?.date?.slice(5);

  return (
    <div data-testid="regen-timeline" className="px-4 pt-3 pb-2 border-t border-white/5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <p className="text-[10px] font-mono-data uppercase tracking-wider text-cyan-300/80 flex items-center gap-1.5">
          <Activity className="w-3 h-3" /> 30-day regeneration timeline
        </p>
        <div className="flex items-center gap-3 text-[9px] font-mono-data">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2 rounded-sm" style={{ background: "#8fd0d6" }} />born {tot.neurons_born ?? 0}</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2 rounded-sm" style={{ background: "#e0708f" }} />pruned {tot.synapses_pruned ?? 0}</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2 rounded-sm" style={{ background: "#7a5a86" }} />decayed {tot.synapses_decayed ?? 0}</span>
        </div>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[150px]" data-testid="regen-timeline-svg">
          <defs>
            <linearGradient id="bornGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#8fd0d6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#9ee3ea" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          <line x1="0" y1={MID} x2={W} y2={MID} stroke="rgba(255,255,255,0.12)" strokeWidth="0.6" />
          {days.map((d, i) => {
            const x = i * 12 + 2, bw = 8;
            const bornH = ((d.neurons_born || 0) / maxBorn) * UP;
            const lost = (d.synapses_pruned || 0) + (d.synapses_decayed || 0);
            const lostH = (lost / maxLost) * DOWN;
            const prunedH = lost ? ((d.synapses_pruned || 0) / lost) * lostH : 0;
            const isHot = hover === i;
            return (
              <g key={i} opacity={hover != null && !isHot ? 0.5 : 1}>
                <rect x={x} y={MID - bornH} width={bw} height={bornH} rx="1" fill="url(#bornGrad)" />
                {prunedH > 0 && <rect x={x} y={MID} width={bw} height={prunedH} rx="1" fill="#e0708f" opacity="0.9" />}
                {lostH - prunedH > 0 && <rect x={x} y={MID + prunedH} width={bw} height={lostH - prunedH} rx="1" fill="#7a5a86" opacity="0.7" />}
                <rect x={x - 2} y="0" width={12} height={H} fill="transparent"
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }} />
              </g>
            );
          })}
        </svg>
        {hover != null && (
          <div className="absolute -top-1 z-10 rounded-lg border border-white/15 bg-black/90 backdrop-blur px-2.5 py-1.5 pointer-events-none shadow-xl"
            style={{ left: `min(${(hover / days.length) * 100}%, calc(100% - 150px))` }}>
            <p className="text-[9px] font-mono-data text-slate-400">{days[hover].date}</p>
            <p className="text-[10px] text-[#9ee3ea]">+{days[hover].neurons_born} neurons born</p>
            <p className="text-[10px] text-[#e0708f]">{days[hover].synapses_pruned} pruned</p>
            <p className="text-[10px] text-[#b79ac6]">{days[hover].synapses_decayed} decayed</p>
          </div>
        )}
        <div className="flex justify-between mt-1">
          <span className="text-[8px] font-mono-data text-slate-600">{lbl(0)}</span>
          <span className="text-[8px] font-mono-data text-slate-600">{lbl(Math.floor(days.length / 2))}</span>
          <span className="text-[8px] font-mono-data text-slate-600">{lbl(days.length - 1)}</span>
        </div>
      </div>
      <p className="text-[8px] font-mono-data text-slate-600 mt-1">inhale = neurons born (real birthdays) · exhale = synapses decayed/pruned (logs daily from now)</p>
    </div>
  );
}

export default function Regeneration() {
  const [d, setD] = useState(null);
  const [tl, setTl] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([api.get("/brain/regeneration"), api.get("/brain/regeneration/timeline")])
      .then(([a, b]) => { setD(a.data); setTl(b.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // live: refresh every 60s
    return () => clearInterval(id);
  }, [load]);

  if (loading && !d) {
    return <div data-testid="regeneration" className="rounded-2xl border border-cyan-400/15 bg-[#04070c]/80 p-6 text-[11px] text-cyan-300/70 animate-pulse mb-6">Reading regeneration telemetry…</div>;
  }
  if (!d) return null;

  const ng = d.neurogenesis || {};
  const rj = d.rejuvenation || {};
  const metab7 = rj.metabolism?.sum_7d || {};
  const decay7 = rj.decay?.sum_7d || {};
  const replay7 = rj.replay?.sum_7d || {};
  const replayLast = rj.replay?.last || {};
  const beliefs = (replay7.beliefs_reassessed || 0) + (metab7.pairs_reassessed || 0);

  return (
    <section data-testid="regeneration" className="rounded-2xl border border-cyan-400/15 bg-gradient-to-b from-[#061019]/80 to-[#04070c]/90 overflow-hidden mb-6">
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 pt-3.5 pb-3">
        <div>
          <h3 className="font-display font-bold text-white text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-300" /> Regeneration &amp; Rejuvenation
            <span className="relative flex h-2 w-2 ml-1">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[9px] font-mono-data text-emerald-400/80 uppercase tracking-wider">live</span>
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Continuous neurogenesis + neocortical rejuvenation, straight from the logs · as of {d.as_of ? d.as_of.slice(11, 16) : "—"} UTC
          </p>
        </div>
        <button data-testid="regeneration-refresh" onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-300 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Neurogenesis */}
      <div className="px-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-mono-data uppercase tracking-wider text-[#9ee3ea] flex items-center gap-1.5"><Dna className="w-3 h-3" /> Hippocampal neurogenesis</p>
          <div className="flex items-center gap-1.5 text-slate-500"><TrendingUp className="w-3 h-3 text-[#8fd0d6]" /><Sparkline series={d.growth_series} field="nodes" /></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <Stat testid="regen-born-24h" icon={Dna} label="neurons born · 24h" value={`+${ng.born_24h ?? 0}`} sub={`+${ng.born_7d ?? 0} this week`} color="#9ee3ea" />
          <Stat testid="regen-fired-24h" icon={Activity} label="neurons fired · 24h" value={ng.fired_24h ?? 0} sub="active working memory" color="#8fd0d6" />
          <Stat testid="regen-rate" icon={TrendingUp} label="growth rate" value={`${ng.rate_per_day ?? 0}/day`} sub="7-day average" color="#7fe0c0" />
          <Stat testid="regen-total" icon={Brain} label="total brain" value={ng.total_nodes ?? 0} sub={`${ng.total_edges ?? 0} synapses`} color="#93b4de" />
        </div>
      </div>

      {/* 30-day breathing timeline */}
      <RegenTimeline tl={tl} />

      {/* Neocortical rejuvenation */}
      <div className="px-4 pt-3 pb-2">
        <p className="text-[10px] font-mono-data uppercase tracking-wider text-[#aea8e0] mb-2 flex items-center gap-1.5"><Waves className="w-3 h-3" /> Neocortical rejuvenation · last 7 days</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <Stat testid="regen-schemas" icon={Brain} label="episodes → schemas" value={metab7.clusters_compressed ?? 0} sub={`${metab7.episodes_pruned ?? 0} raw episodes distilled`} color="#aea8e0" />
          <Stat testid="regen-beliefs" icon={Activity} label="beliefs reassessed" value={beliefs} sub="Bayesian re-weighting" color="#c0a8e0" />
          <Stat testid="regen-pruned" icon={Scissors} label="synapses pruned" value={decay7.pruned ?? 0} sub={`${decay7.decayed ?? 0} decayed`} color="#e0a8c0" />
          <Stat testid="regen-replay" icon={Waves} label="episodes replayed" value={replay7.replayed ?? 0} sub={replayLast.mean_prediction_error != null ? `pred-error ${replayLast.mean_prediction_error}` : "prediction-error tuned"} color="#8fd0d6" />
        </div>
      </div>

      {/* All-resource trawl */}
      <div className="px-4 pt-3 pb-4 border-t border-white/5">
        <p className="text-[10px] font-mono-data uppercase tracking-wider text-cyan-300/80 mb-2 flex items-center gap-1.5">
          <GitBranch className="w-3 h-3" /> All-resource trawl · which reservoirs fed the brain
        </p>
        <div className="flex flex-wrap gap-2">
          {(d.reservoirs || []).map((r) => {
            const meta = RES_META[r.name] || { icon: Globe, label: r.name };
            const Icon = meta.icon;
            const active = !!r.last_run;
            return (
              <div key={r.name} data-testid={`regen-reservoir-${r.name}`}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${active ? "border-cyan-400/20 bg-cyan-400/[0.04]" : "border-white/8 bg-white/[0.02] opacity-60"}`}>
                <Icon className={`w-3.5 h-3.5 ${active ? "text-cyan-300" : "text-slate-500"}`} />
                <div className="leading-tight">
                  <p className="text-[10px] text-slate-200">{meta.label}</p>
                  <p className="text-[8px] font-mono-data text-slate-500">
                    {active ? `fed ${ago(r.last_run)} · +${r.concepts_added_total || 0}` : (r.name === "github" ? "queued next" : "idle")}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[9px] font-mono-data text-slate-600 mt-2">
          {d.trawl?.projects_trawled ?? 0}/{d.trawl?.fleet_size ?? 0} ships trawled · web grounding {d.web_research_enabled ? "on" : "off"}
        </p>
      </div>
    </section>
  );
}
